import { gql, useMutation } from "@apollo/client";
import { Button, createStyles, IconButton, Link, makeStyles, Menu, MenuItem, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tooltip, Typography } from "@material-ui/core";
import MoreIcon from '@material-ui/icons/MoreHoriz';
import { useLocation } from "@reach/router";
import moment from "moment";
import * as qs from 'query-string';
import React from "react";
import { useList } from "../../apollo/networking";
import { useAppDialog } from "../../app-dialog";
import { useOrg } from "../../auth/use-org";
import { EventResourceFragment, EventResourceModel } from "../../models/event-resource.model";
import { EventModel } from "../../models/event.model";
import { DeleteConfirmDialog } from "../../ui/delete-confirm.dialog";
import { LinkifyTruncateText } from '../../ui/linkify-truncate-text';
import { LoadingDots } from "../../ui/loading-dots";
import { TicketEditorDialog } from "./resource-editor";

type Props = {
    event: EventModel;
}

const MutationUpdateTicket = gql`
    mutation update_resource($resource_id: bigint!, $set: event_resource_set_input!) {
        update_event_resource_by_pk(
            pk_columns: {id: $resource_id},
            _set: $set,
        ) {
            ${EventResourceFragment}
        }
    }
`;

export const ResourcesList = ({
    event,
}: Props) => {
    const classes = useStyles({});
    const location = useLocation();
    const params = qs.parse(location.search || '');
    const add = params?.add;
    const app_dialog = useAppDialog();
    const [confirm_delete, setConfirmDelete] = React.useState<EventResourceModel | false>(false);
    const [anchor_el, setAnchorEl] = React.useState<{
        anchor: HTMLButtonElement;
        item: EventResourceModel;
    } | undefined>();
    const {
        org: {
            currency,
        }
    } = useOrg();
    const [updateTicket, update_ticket_status] = useMutation(MutationUpdateTicket);
    const [editor, setEditor] = React.useState<{
        action: 'edit' | 'create';
        item?: EventResourceModel;
    } | undefined>();
    const {
        items,
        loading,
        refetch,
        error,
    } = useList<EventResourceModel>({
        entity_name: 'event_resource',
        fragment: EventResourceFragment,
        clause_outer: `$event_id: bigint!`,
        variables: {
            event_id: event.id,
        },
        query_inner: `
            where: {event_id: {_eq: $event_id}},
            order_by: {id: asc}
        `
    });

    React.useEffect(() => {
        if (!!add) {
            setEditor({ action: 'create' });
        }
    }, [add])

    const onFinish = (_: EventResourceModel, is_new?: boolean) => {
        if (is_new) {
            refetch();
        }
        setEditor(undefined);
    }

    const onEdit = (item?: EventResourceModel) => () => {
        if (!item) {
            return;
        }
        setAnchorEl(undefined);
        setEditor({
            action: 'edit',
            item,
        })
    }

    const onDelete = () => {
        if (!anchor_el) {
            return;
        }
        setConfirmDelete(anchor_el.item);
        setAnchorEl(undefined);
    }

    const onConfirmDelete = async () => {
        if (!confirm_delete) {
            return;
        }
        try {
            await updateTicket({
                variables: {
                    resource_id: confirm_delete.id,
                    set: {
                        deleted_at: 'now()',
                    }
                },
            });
            setConfirmDelete(false);
            app_dialog.showSnackbar('Resource deleted');
            refetch();
        } catch (e: any) {
            app_dialog.showError(e);
        }
    }

    return <>
        {JSON.stringify(error)}
        {confirm_delete ? <DeleteConfirmDialog
            onCancel={() => setConfirmDelete(false)}
            onConfirm={onConfirmDelete}
            message={`Are you sure you want to delete the resource: ${confirm_delete.name}. This is not reversible.`}
            deleting={update_ticket_status.loading}
            confirm_text='confirm'
        /> : null}
        <Menu
            open={Boolean(anchor_el)}
            anchorEl={anchor_el?.anchor}
            onClose={() => setAnchorEl(undefined)}
        >
            <MenuItem onClick={onEdit(anchor_el?.item)}>Edit</MenuItem>
            <MenuItem onClick={onDelete}><Typography color='secondary'>Delete</Typography></MenuItem>
        </Menu>
        {editor ? <TicketEditorDialog
            action={editor.action}
            item={editor.item}
            event_id={event.id}
            onFinish={onFinish}
            onClose={() => setEditor(undefined)} /> : null}
        <Button
            variant='outlined'
            color='primary'
            className={classes.addButton}
            onClick={() => setEditor({
                action: 'create',
            })}>+ Add Resource</Button>
        <TableContainer>
            <Table className={classes.table} size="small" aria-label="a dense table">
                <TableHead>
                    <TableRow className='header'>
                        <TableCell />
                        <TableCell style={{ width: 50 }}>
                            ID</TableCell>
                        <TableCell >Created</TableCell>
                        <TableCell >Name</TableCell>
                        <TableCell >Link</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {items.map((item, index) => (
                        <TableRow
                            key={item.id}
                            hover
                        >
                            <TableCell>
                                <div className={classes.buttons}>
                                    <Tooltip title='More Options'>
                                        <IconButton size='small' onClick={(e) => setAnchorEl({
                                            anchor: e.currentTarget,
                                            item,
                                        })}>
                                            <MoreIcon fontSize='small' />
                                        </IconButton>
                                    </Tooltip>
                                </div>
                            </TableCell>
                            <TableCell style={{ width: 50 }}>
                                {item.id}
                            </TableCell>
                            <TableCell >{moment(item.created_at).calendar()}</TableCell>
                            <TableCell >{item.name}</TableCell>
                            <TableCell style={{ minWidth: 300 }} ><Link
                                target='_blank' href={item.link}>{item.link}</Link></TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </TableContainer>
        {loading ? <LoadingDots /> : null}
    </>
}

const useStyles = makeStyles(theme => createStyles({
    table: {
        width: '100%',
        background: 'white',
        border: `1px solid ${theme.palette.divider}`,
        '& .header': {
            background: theme.palette.background.paper,
        }
    },
    buttons: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        width: 20,
    },
    addButton: {
        marginBottom: theme.spacing(2),
    }
}))